import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { joinWords } from '@features/Dashboard';
import { cn } from '@shared/lib';
import type { DayPlan } from '@shared/types';
import { FlameIcon, Modal } from '@shared/ui';
import type { BossStats, MistakeWord, SessionStats } from '../model';

/** Сколько слов с ошибками показываем прямо на итогах. Остальные — в модалке:
 *  длинный список иначе выдавливает кнопку «Готово» за нижний край. */
const INLINE_MISTAKES = 6;

interface CompletionProps {
  stats: SessionStats;
  bossStats: BossStats | null;
  /** Лучшее комбо за сессию (упражнения + финальный раунд). */
  bestCombo: number;
  /** Слова, на которых ошиблись. Пусто — прошли всё с первого раза. */
  mistakeWords: MistakeWord[];
  /** Длительность захода в мс. */
  elapsedMs: number;
  onExit: () => void;
  withKeyboard: boolean;
  /** Серия по дням МСК (из сводки главной; после этой тренировки сводка перечитана). */
  streak?: number;
  /** Сводка дня после этой тренировки: сколько слов попросится в повтор завтра и какие первыми
   *  (решение владельца 2026-10-21, по истории тренировок). */
  tomorrow?: DayPlan | null;
}

/** Итоги тренировки: четыре цифры, строка про финальный раунд и список слов,
 *  на которых споткнулись. Повторить их отдельной кнопкой нельзя — следующая
 *  тренировка соберётся в конструкторе. */
export function Completion({
  stats,
  bossStats,
  bestCombo,
  mistakeWords,
  elapsedMs,
  onExit,
  withKeyboard,
  streak = 0,
  tomorrow = null,
}: CompletionProps) {
  const { t, i18n } = useTranslation();
  const [allOpen, setAllOpen] = useState(false);
  const firstWords = tomorrow?.tomorrowFirst ?? [];

  useEffect(() => {
    if (!withKeyboard) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      onExit();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onExit, withKeyboard]);

  // seen===0 (например, вход сразу на финальный раунд без ответов) — точности
  // нет, показываем «—», а не лживые 100%.
  const acc = stats.seen ? Math.round((stats.correct * 100) / stats.seen) : 0;
  const headline =
    acc >= 90
      ? t('session.complete.headlineClean')
      : acc >= 70
        ? t('session.complete.headlineGood')
        : t('session.complete.headlineWork');
  const sub = mistakeWords.length
    ? t('session.complete.subReturn', { count: mistakeWords.length })
    : t('session.complete.subPerfect');
  const minutes = Math.max(1, Math.round(elapsedMs / 60000));

  return (
    <main className="st-body st-done">
      <div className="st-eyebrow">
        {t('session.complete.kicker')} · {t('session.complete.minutes', { count: minutes })}
      </div>
      <h1 className="st-done-title">{headline}</h1>
      <p className="st-done-sub">{sub}</p>

      <div className="st-tiles">
        <div className="st-tile">
          <span className={cn('st-tile-v', acc >= 70 ? 'is-good' : 'is-warn')}>
            {stats.seen === 0 ? '—' : `${acc}%`}
          </span>
          <span className="st-tile-l">{t('session.complete.accuracy')}</span>
        </div>
        <div className="st-tile">
          <span className="st-tile-v">{stats.seen}</span>
          <span className="st-tile-l">{t('session.complete.answers')}</span>
        </div>
        <div className="st-tile">
          <span className="st-tile-v is-bad">{stats.wrong}</span>
          <span className="st-tile-l">{t('session.complete.wrong', 'Ошибок')}</span>
        </div>
        <div className="st-tile">
          <span className="st-tile-v is-accent">×{bestCombo}</span>
          <span className="st-tile-l">{t('session.complete.bestRun')}</span>
        </div>
      </div>

      {bossStats && bossStats.totalPacks > 0 && (
        <div className="st-done-boss">
          {t('session.complete.bossLine', {
            clean: bossStats.packsClean,
            total: bossStats.totalPacks,
            seconds: Math.round((bossStats.durationMs || 0) / 1000),
          })}
        </div>
      )}

      {mistakeWords.length > 0 && (
        <div className="st-mistakes">
          <div className="st-mistakes-l">
            {t('session.complete.mistakes')}
            <span>{mistakeWords.length}</span>
          </div>
          <MistakeList words={mistakeWords.slice(0, INLINE_MISTAKES)} />
          {mistakeWords.length > INLINE_MISTAKES && (
            <button type="button" className="st-mistakes-all" onClick={() => setAllOpen(true)}>
              {t('session.complete.showAll', { count: mistakeWords.length })}
            </button>
          )}
        </div>
      )}

      <Modal
        open={allOpen}
        onClose={() => setAllOpen(false)}
        kicker={t('session.complete.mistakes')}
        title={t('session.complete.mistakesTitle', { count: mistakeWords.length })}
        width={560}
      >
        <div className="st-mistakes-scroll">
          <MistakeList words={mistakeWords} />
        </div>
      </Modal>

      {/* Повод вернуться завтра (решение владельца 2026-10-21): серия и сколько слов попросится в повтор —
          те же цифры, что на главной. */}
      {tomorrow && tomorrow.tomorrow > 0 && (
        <div className="st-tomorrow">
          {streak > 0 && (
            <span className="st-tomorrow-streak">
              <FlameIcon className="h-4 w-4" aria-hidden />
              {t('today.streak', { count: streak })}
            </span>
          )}
          <span className="st-tomorrow-line">
            {t('today.tomorrowTitle', { count: tomorrow.tomorrow })}
            {firstWords.length > 0 &&
              ` · ${t('today.firstShort', { words: joinWords(firstWords, i18n.language) })}`}
          </span>
        </div>
      )}

      <button type="button" className="st-cta" onClick={onExit}>
        {t('session.complete.done', 'Готово')}
        {withKeyboard && <kbd>Enter</kbd>}
      </button>
    </main>
  );
}

/** Строки «слово — перевод». Одна разметка на итоги и на модалку. */
function MistakeList({ words }: { words: MistakeWord[] }) {
  return (
    <div className="st-mistakes-list">
      {words.map((w) => (
        <div key={w.id} className="st-mistake">
          <span className="st-mistake-en">{w.en.toLowerCase()}</span>
          <span className="st-mistake-ru">{w.ru.toLowerCase()}</span>
        </div>
      ))}
    </div>
  );
}
